import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';

const Footer = () => {
  const whatsappNumber = '919927058909';
  
  // Function to get the appropriate WhatsApp link based on device
  const getWhatsAppLink = () => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      return `https://wa.me/${whatsappNumber}`;
    } else {
      return `https://web.whatsapp.com/send?phone=${whatsappNumber}`;
    }
  };

  return (
    <>
      {/* WhatsApp Float Button */}
      <a
        href={getWhatsAppLink()}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-3 sm:p-4 shadow-lg transition-all duration-300 hover:scale-110 z-50"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
      </a>

      <footer className="bg-black text-white">
        {/* Trust Badges */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 py-6 sm:py-8 md:py-12">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-3 gap-3 sm:gap-6 md:gap-8 text-center">
              <div className="flex flex-col items-center space-y-1 sm:space-y-2">
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-yellow-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xs sm:text-sm md:text-xl font-bold text-yellow-500">2-7 Days</h3>
                <p className="text-[10px] sm:text-xs md:text-base text-gray-400">Fast Shipping</p>
              </div>
              <div className="flex flex-col items-center space-y-1 sm:space-y-2">
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-yellow-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xs sm:text-sm md:text-xl font-bold text-yellow-500">100% Authentic</h3>
                <p className="text-[10px] sm:text-xs md:text-base text-gray-400">Since 1975</p>
              </div>
              <div className="flex flex-col items-center space-y-1 sm:space-y-2">
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-yellow-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xs sm:text-sm md:text-xl font-bold text-yellow-500">Free Shipping</h3>
                <p className="text-[10px] sm:text-xs md:text-base text-gray-400">Above ₹2000</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Footer */}
        <div className="container mx-auto px-4 py-8 sm:py-10 md:py-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 sm:gap-8">
            {/* About */}
            <div className="col-span-2 md:col-span-1">
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
                <span className="text-yellow-500">JAIN</span> WATCH
              </h3>
              <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">
                Trusted Since 1975, Jain Watch Company is your authorized retailer for premium wristwatches.
              </p>
              <div className="flex space-x-3 sm:space-x-4">
                <a href="https://www.facebook.com/share/19ymBEJ2WN/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-yellow-500 transition-colors">
                  <Facebook className="w-5 h-5 sm:w-6 sm:h-6" />
                </a>
                <a href="https://www.instagram.com/jainwatchcompany?igsh=MTdzNmkzZGxmYmxvcw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-yellow-500 transition-colors">
                  <Instagram className="w-5 h-5 sm:w-6 sm:h-6" />
                </a>
                <a href="https://youtube.com/@deepakjain8880?si=W9UGBp_XKcu3XBxV" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-yellow-500 transition-colors">
                  <Youtube className="w-5 h-5 sm:w-6 sm:h-6" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm sm:text-base md:text-lg font-bold mb-3 sm:mb-4 text-yellow-500">Quick Links</h4>
              <ul className="space-y-1.5 sm:space-y-2">
                <li>
                  <Link to="/" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/products" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    All Products
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            {/* Categories */}
            <div>
              <h4 className="text-sm sm:text-base md:text-lg font-bold mb-3 sm:mb-4 text-yellow-500">Categories</h4>
              <ul className="space-y-1.5 sm:space-y-2">
                <li>
                  <Link to="/products/Men" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    Men's Watches
                  </Link>
                </li>
                <li>
                  <Link to="/products/Women" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    Women's Watches
                  </Link>
                </li>
                <li>
                  <Link to="/products/Kids" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    Kids' Watches
                  </Link>
                </li>
                <li>
                  <Link to="/products/Smart Watches" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    Smart Watches
                  </Link>
                </li>
              </ul>
            </div>

            {/* Policy */}
            <div>
              <h4 className="text-sm sm:text-base md:text-lg font-bold mb-3 sm:mb-4 text-yellow-500">Policy</h4>
              <ul className="space-y-1.5 sm:space-y-2">
                <li>
                  <Link to="/shipping-policy" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    Shipping Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-conditions" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/cancellation-refund" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    Cancellation & Refund
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="col-span-2 md:col-span-1">
              <h4 className="text-sm sm:text-base md:text-lg font-bold mb-3 sm:mb-4 text-yellow-500">Contact Info</h4>
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex items-start space-x-2 sm:space-x-3">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                    Jain Watch Company, Ashoka Market, Street No. 4, Khatauli, Dist. Muzaffarnagar, UP - 251201
                  </span>
                </li>
                <li className="flex items-center space-x-2 sm:space-x-3">
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 flex-shrink-0" />
                  <a href="tel:9927058909" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm">
                    +91 9927058909
                  </a>
                </li>
                <li className="flex items-center space-x-2 sm:space-x-3">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 flex-shrink-0" />
                  <a href="mailto:jainwatchcompany1975@gmail.com" className="text-gray-400 hover:text-yellow-500 transition-colors text-xs sm:text-sm break-all">
                    jainwatchcompany1975@gmail.com
                  </a>
                </li>
              </ul>
              {/* Google Map */}
              <div className="mt-4 rounded-lg overflow-hidden">
                <a 
                  href="https://maps.app.goo.gl/Q8LCHVZZvT59ddqW6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block relative"
                >
                  <div className="absolute inset-0 z-10 cursor-pointer"></div>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3480.8841896396!2d77.27149067550474!3d29.276099075398!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390c5d0c8f0d5255%3A0x8b6f7b4c8a5d5e5a!2sAshoka%20Market%2C%20Khatauli%2C%20Uttar%20Pradesh%20251201!5e0!3m2!1sen!2sin!4v1703123456789!5m2!1sen!2sin"
                    width="100%"
                    height="120"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Jain Watch Company Location"
                    className="rounded-lg"
                  ></iframe>
                </a>
                <a 
                  href="https://maps.app.goo.gl/Q8LCHVZZvT59ddqW6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block mt-2 text-center text-xs text-yellow-500 hover:text-yellow-400 transition-colors"
                >
                  Open in Google Maps →
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800">
          <div className="container mx-auto px-4 py-4 sm:py-6">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
              <p className="text-gray-400 text-[10px] sm:text-xs md:text-sm text-center md:text-left">
                © 2025 Jain Watch Company. All rights reserved.
              </p>
              <div className="flex space-x-4 sm:space-x-6">
                <Link to="/privacy-policy" className="text-gray-400 hover:text-yellow-500 text-[10px] sm:text-xs md:text-sm transition-colors">
                  Privacy
                </Link>
                <Link to="/terms-conditions" className="text-gray-400 hover:text-yellow-500 text-[10px] sm:text-xs md:text-sm transition-colors">
                  Terms
                </Link>
                <Link to="/cancellation-refund" className="text-gray-400 hover:text-yellow-500 text-[10px] sm:text-xs md:text-sm transition-colors">
                  Refund
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
